// app/shared/components/forms/HFInputComponent.tsx
'use client';

import * as React from 'react';
import { Control, Controller, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form';

import { Input as ShadcnInput, type InputProps as ShadcnInputProps } from '@/components/ui/input';
import { Label as ShadcnLabel } from '@/components/ui/label';
import { cn } from '@/lib/utils';

// Extend ShadcnInputProps to allow passing any valid Input prop
// Omit RHF-controlled props from ShadcnInputProps to avoid conflicts
interface HFInputComponentProps<TFieldValues extends FieldValues>
  extends Omit<ShadcnInputProps, 'name' | 'defaultValue' | 'value' | 'onChange' | 'onBlur' | 'ref'> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label?: string;
  placeholder?: string;
  className?: string; // For the wrapper div
  inputClassName?: string; // For customizing the input element further
  disabled?: boolean;
  type?: React.HTMLInputTypeAttribute; // Explicitly include type
  autoComplete?: string; // Add autocomplete support
  rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>; // Add validation rules support
}

export function HFInputComponent<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  className,
  inputClassName,
  disabled = false,
  type = 'text',
  autoComplete,
  rules,
  ...rest // Pass through any other compatible ShadcnInputProps
}: HFInputComponentProps<TFieldValues>) {
  const inputId = name; // Use the RHF field name as a unique ID for the label

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <div className={cn('space-y-1.5', className)}>
          {label && (
            <ShadcnLabel htmlFor={inputId} className="text-gray-800 font-medium">
              {label}
            </ShadcnLabel>
          )}
          <ShadcnInput
            {...field} // Spread field props (onChange, onBlur, value, ref)
            {...rest}  // Spread any other pass-through props
            id={inputId}
            type={type}
            placeholder={placeholder}
            autoComplete={autoComplete}
            disabled={disabled || field.disabled}
            className={cn(
              "w-full h-10 px-3 py-2 text-base rounded-md shadow-sm",             // Base structure - text-base (16px) prevents Safari zoom
              "bg-[#fafafa] text-gray-900",                               // Default app-specific styles
              error ? "border-error ring-1 ring-error"                        // Error state styles (takes precedence for border)
                    : "border-gray-300",                                      // Default border if no error
              // Interaction styles: focus-visible is preferred for accessibility 
              // and to avoid ring on programmatic focus or mouse click focus if not desired.
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:border-transparent", 
              // We can remove hover:border-secondary if focus-visible is comprehensive enough
              // or keep it for a subtle hover effect if desired. Let's keep it for now.
              !error ? "hover:border-secondary" : "", // Hover border only if no error
              (disabled || field.disabled) ? "disabled:cursor-not-allowed disabled:opacity-50 bg-gray-100 border-gray-300" : "", // Disabled state styles, ensure border is set
              inputClassName // Allow overrides for the input itself
            )}
            value={field.value === null || field.value === undefined ? '' : field.value}
          />
          {error && <p className="mt-1 text-xs text-error">{error.message}</p>}
        </div>
      )}
    />
  );
} 